
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";

import { PrimaryButton, SecondaryButton } from "../styles/basicComponents";
import { OpenApiRequestStatus, useOpenApiRequest } from "typescript-request-utils";
import { getApiRegistry } from "../framework/state/apiRegistry";
import { UserRepresentation } from "../api/models/UserRepresentation";
import { getCurrentUser } from "../features/basic/reducers/basicReducer";
import NewApplication from "../framework/components/view/NewApplication";
import { ApplicationStatus } from "../api/enums/ApplicationStatus";
import { RepresentationRequestType } from "../api/enums/RepresentationRequestType";
import { Relation } from "../api/enums/Relation";
import { BatchApplication } from "../api/models/BatchApplication";
import LogoIconComponent from "../framework/components/iconComponents/LogoIconComponent";
import { BackArrowCreateApplicationIcon, CheckmarkIcon } from "../framework/components/modal/modalIcons";
import { useTheme, useVariables } from "../features/context/CustomerContext";
import useCustomTranslation from "../features/hooks/CustomUseTranslation";

const Div = styled.div`
    display: flex;
    flex-direction: column;
    @media screen and (min-width: ${props => props.theme.mobileMenuBreakpoint}px) {
        box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.25);
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        margin: 0px auto 60px auto;
        width: 1035px;
        font-size: 16px;
    }
    @media screen and (min-width: ${props => props.theme.mobileMenuBreakpoint}px) {
        width: auto;
        margin: 0px 250px 60px 250px;
    }
    font-size: 12px;
    margin: 20px;
    background-color: white;
    height: calc(100% - 260px);
`
const BackButtonContainer = styled.div`
    display: flex;
    padding: 21px 75px 0 30px;
    @media screen and (max-width: 470px) {
        padding: 20px 0 0 0;
    }
`
const ButtonContainer = styled.div`
    margin: 1rem 0;
    text-align: center;
    @media screen and (max-width: 470px) {
        padding: 20px 0 0 0;
    }
`
const Body = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    flex-grow: 1;
    overflow-y: auto;

    ::-webkit-scrollbar {
        width: 5px;
        height: 5px;
    }

    ::-webkit-scrollbar-track {
        background: transparent; /* You can change this to a desired color */
    }

    ::-webkit-scrollbar-thumb {
        background: ${props => props.theme.brandColor1};
        border-radius: 5px;
    }
`
const IntroductionTextSection = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 603px;
    margin: 0 auto;
    div:not(:last-child) {
        margin-bottom: 10px;
    }
`
const IntroductionText = styled.div`
    text-align: initial;
`
const ToastMessage = styled.p`
    font-weight: 400;
    margin-bottom: 10px;
    height: auto !important;
    margin-left: 5px;
`
const ToastTextContainer = styled.div`
    @media screen and (max-width: 1440px) {
        width: 150px;
        left: 50px;
        height: 50px;
    }
    bottom: 20px;
    left: 57px;
    position: relative;
    width: 400px;
`
const AddApplicationButton = styled(PrimaryButton)`
    padding-right: 0px;
    padding-left: 0px;
    border-radius: 100px;
    width: 260px;
    height: 57px;
    font-weight: 700;
    font-size: 16px;
    line-height: 18px;
    display: list-item;
    margin: 0px;
    @media screen and (max-width: 800px) {
        margin-left: 0;
    }
`
const ApplicationsContainer = styled.div`
    display: grid;
    row-gap: 1rem;
    margin-top: 2rem;
    width: 100%;
    max-width: 850px;
    margin: 0 auto;
    @media screen and (max-width: 800px) {
        max-width: 650px;
    }
`

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 1rem;
`

const ContinueButton = styled(PrimaryButton)`
    @media screen and (max-width: 470px) {
        width: 100%; 
        margin: 20px 0 0 0;
    }
    font-weight: 700;
    font-size: 16px;
    line-height: 18px;
    margin-top: 0;
    margin-bottom: 0;
`

const BackButton = styled(SecondaryButton)`
    @media screen and (max-width: 470px) {
        width: 100%; 
        margin: 20px 0 0 0;
    }
    font-weight: 700;
    font-size: 16px;
    line-height: 18px;
    margin-top: 0;
    margin-bottom: 0;
`

const CreateApplicationView: React.FC = () => {
    var currentDate = new Date();
    var maxYearsFromNow = new Date();
    const variables = useVariables();
    maxYearsFromNow.setFullYear(currentDate.getFullYear() + variables.AUTHORIZATION_MAX_PERIOD_OF_VALIDITY_IN_YEARS);

    const history = useHistory();
    const { t } = useCustomTranslation();

    const [applicationsWithErrorsExist, setApplicationsWithErrorsExist] = useState<boolean>(false);
    const [applicationsWithOwnUsernameErrorsExist, setApplicationsWithOwnUsernameErrorsExist] = useState<boolean>(false);
    const [emptyFieldExists, setEmptyFieldExists] = useState<boolean>(true);
    const [currentUser, setCurrentUser] = useState<UserRepresentation>({});
    const [applicationResponse, setApplicationResponse] = useState<BatchApplication[]>();
    const [applications, setApplications] = useState<BatchApplication[]>([createNewEmptyApplication()]);
    const [applicationRequest, updateApplications] = useOpenApiRequest(getApiRegistry().applicationsApi.updateMultiple);

    const currentUserFromState = useSelector(getCurrentUser);

    useEffect(() => {
        if (currentUserFromState) {
            setCurrentUser(currentUserFromState);
        }
        // eslint-disable-next-line
    }, [currentUserFromState]);

    useEffect(() => {
        if (currentUser) {
            setApplications([createNewEmptyApplication()]);
        }
        // eslint-disable-next-line
    }, [currentUser]);


    useEffect(() => {
        if (applicationRequest.type === OpenApiRequestStatus.Complete) {
            setApplicationResponse(applicationRequest.responsePayload);
        }
        // eslint-disable-next-line
    }, [applicationRequest])


    useEffect(() => {
        setApplications([{}]);
        var applicationsWithErrors: BatchApplication[] = [];
        if (applicationResponse) {
            for (let item of applicationResponse) {
                if (item.errorCode) {
                    item.updateRequired = true;
                    applicationsWithErrors.push(item);
                }
                else {
                    showToastMessage();
                }
            }
            if (applicationsWithErrors.length > 0) {
                setApplicationsWithErrorsExist(true);
                setApplications(applicationsWithErrors);
            }
            else {
                localStorage.setItem('showToast', 'personalApplication');
                history.push('sentApplications');
            }
        }
        // eslint-disable-next-line
    }, [applicationResponse])

    useEffect(() => {
        const applicationContainer = document.getElementById("application-container");
        if(applicationContainer) {
            const last = applicationContainer.children[applicationContainer.children.length - 1];
            if(last) {
                (last as HTMLElement).scrollIntoView({ behavior: 'smooth' });
            }
        } 
    }, [applications.length])

    function addApplication() {
        setEmptyFieldExists(true);
        if (applications) {
            setApplications([...applications, createNewEmptyApplication()]);
        }
        else {
            setApplications([createNewEmptyApplication()]);
        }
    }

    function createNewEmptyApplication() {
        return { requesteeUsername: "", requesteeCustomerNumber: "", relation: Relation.REPRESENTATIVE,
            requestorUser: currentUser, status: ApplicationStatus.PENDING, startDate: currentDate,
            endDate: maxYearsFromNow, representationRequestType: RepresentationRequestType.USER_REQUEST
        }
    }

    function updateApplication(application: BatchApplication, id: number) {
        var listMpHasEmptyFields = false;
        var listHasOwnUsernameError = false;
        var listErrors = false;
        let updatedList = applications?.map((item, index) => {
            if (index === id) {
                if (application.requesteeUsername === currentUser.username) {
                    listHasOwnUsernameError = true;
                }
                if(application.updateRequired) {
                    application.updateRequired = false;
                }
                if (!application.requesteeCustomerNumber || application.requesteeCustomerNumber === "" || !application.requesteeUsername || application.requesteeUsername === "") {
                    listMpHasEmptyFields = true;
                }
                return { ...item, ...application };
            }
            if (item.updateRequired) {
                listErrors = true;
            }
            if (!item.requesteeCustomerNumber || item.requesteeCustomerNumber === "" || !item.requesteeUsername || item.requesteeUsername === "") {
                listMpHasEmptyFields = true;
            }
            return item;
        });
        setApplicationsWithOwnUsernameErrorsExist(listHasOwnUsernameError);
        setEmptyFieldExists(listMpHasEmptyFields);
        setApplicationsWithErrorsExist(listErrors);
        setApplications(updatedList);
    }

    function deleteApplication(id: number) {
        var listMpHasEmptyFields = false;
        var listErrors = false;
        if (applications?.length === 1) {
            setApplications([createNewEmptyApplication()]);
            setEmptyFieldExists(true);
        }
        else {
            const updatedList = applications?.filter((_, index) => index !== id);
            for (let item of updatedList) {
                if (!item.requesteeCustomerNumber || item.requesteeCustomerNumber === "" || !item.requesteeUsername || item.requesteeUsername === "") {
                    listMpHasEmptyFields = true;
                }
                if (item.updateRequired) {
                    listErrors = true;
                }
            }
            setEmptyFieldExists(listMpHasEmptyFields);
            setApplicationsWithErrorsExist(listErrors);
            setApplications(updatedList);
        }
    }

    function modifyAndUpdateApplications() {
        var updatedList: BatchApplication[] = [];
        for (let item of applications) {
            updatedList.push({ requesteeUsername: item.requesteeUsername, requesteeCustomerNumber: item.requesteeCustomerNumber, relation: Relation.REPRESENTATIVE, requestorUser: currentUser, status: ApplicationStatus.PENDING, startDate: item.startDate, endDate: item.endDate, representationRequestType: RepresentationRequestType.USER_REQUEST });
        }
        updateApplications(updatedList);
    }

    const showToastMessage = () => {
        toast.success(
            <div>
                <CheckmarkIcon />
                <ToastTextContainer>
                    <ToastMessage>{t('ApplicationSuccessToastFirst')}</ToastMessage>
                    <ToastMessage>{t('ApplicationSuccessToastSecond')}</ToastMessage>
                </ToastTextContainer>
            </div>, {
            className: "Toastify__toast-container--personal",
            position: toast.POSITION.TOP_RIGHT,
            icon: false,
            hideProgressBar: true,
            autoClose: 15000
        });
    };

    const theme = useTheme();

    return (
        <Div theme={theme}>
            <ToastContainer />
            <BackButtonContainer>
                <BackArrowCreateApplicationIcon theme={theme}
                    type="button"
                    onClick={() => history.goBack()}>{t("Back")}
                </BackArrowCreateApplicationIcon>
                {variables.showCustomerLogoOnRepresentationPages &&
                        <LogoIconComponent style={{ marginLeft: 'auto', marginRight: 'auto', marginBottom: '1rem' }}></LogoIconComponent>
                }
            </BackButtonContainer>
            <Body theme={theme}>
                <FlexColumn>
                    <IntroductionTextSection>
                        <IntroductionText> {t("Create application introduction text first line")}</IntroductionText>
                        <IntroductionText> {t("Create application introduction text second line")}</IntroductionText>
                        <IntroductionText> {t("Create application introduction text third line")}</IntroductionText>
                    </IntroductionTextSection>
                    <ApplicationsContainer id="application-container">
                        {applications?.map((application: BatchApplication, index) => (
                            <NewApplication
                                key={index}
                                id={index}
                                application={application}
                                setApplication={updateApplication}
                                deleteApplication={deleteApplication}
                                currentUser={currentUser.username}
                            />
                        ))}
                        <AddApplicationButton theme={theme}
                            type="button"
                            onClick={() => { addApplication() }}>{t("Add new application")}
                        </AddApplicationButton>
                    </ApplicationsContainer>
                </FlexColumn>
            </Body>
            <ButtonContainer>
                <BackButton theme={theme} onClick={() => history.goBack()}>{t("Back")}</BackButton>
                <ContinueButton
                    theme={theme}
                    disabled={applicationRequest.type === OpenApiRequestStatus.Pending || emptyFieldExists || applicationsWithOwnUsernameErrorsExist || applicationsWithErrorsExist}
                    style={{ opacity: (applicationRequest.type === OpenApiRequestStatus.Pending || emptyFieldExists || applicationsWithOwnUsernameErrorsExist || applicationsWithErrorsExist) ? '50%' : '100%' }}
                    onClick={() => modifyAndUpdateApplications()}>{t("Send")}
                </ContinueButton>
            </ButtonContainer>
        </Div>
    )
}

export default CreateApplicationView;